<template>
    <div
        class="image"
        :class="{ dragHover }"
        v-on:drop="onDrop"
        v-on:dragover="onDragOver"
        v-on:dragleave="onDragLeave"
    >
        <div v-if="imageUrl && !uploadCancellationToken" class="inline-block mb-3">
            <img :src="imageUrl" class="rounded-md shadow-md" :class="imgClass" />

            <!-- Alt text — saved to the media's name (same convention as the media library's alt field). -->
            <input
                type="text"
                :name="altInputName"
                v-model="alt"
                placeholder="Alt tekst (beschrijving voor toegankelijkheid)"
                aria-label="Alt tekst"
                class="block w-full mt-2 px-3 py-1.5 text-sm rounded-md focus:outline-none"
                :class="altWarning ? 'border border-orange-400 focus:border-orange-400' : 'border border-gray-300 focus:border-indigo-500'"
            />

            <!-- WCAG hint: empty alt, or an alt that's still just the filename. -->
            <p v-if="altWarning" class="mt-1 text-xs text-orange-600">
                <i class="far fa-exclamation-triangle mr-1"></i>
                Geef een korte, beschrijvende alt-tekst (niet alleen de bestandsnaam) — vereist voor toegankelijkheid (WCAG).
            </p>
        </div>

        <br v-if="imageUrl" />

        <label
            class="relative flex h-32 p-0 text-sm text-center text-gray-400 transition border-2 border-gray-300 border-dashed rounded-md cursor-pointer dropzone bg-gray-50 hover:bg-gray-100 hover:text-gray-500"
            ref="dropzone"
            v-show="!imageUrl || uploadCancellationToken"
            @click="uploadCancellationToken ? cancelUpload($event) : null"
        >
            <div class="px-2 m-auto">
                <template v-if="uploadCancellationToken && uploadPercentage < 100">
                    <div class="mb-2 bg-gray-200 rounded">
                        <div class="h-2 bg-green-500 rounded" :style="{ width: uploadPercentage + '%' }"></div>
                    </div>

                    Bezig met uploaden...<br />
                    <span class="text-xs text-gray-400">Klik om te annuleren.</span>
                </template>

                <template v-else-if="uploadCancellationToken && uploadPercentage == 100">
                    <i class="text-2xl fa fa-circle-notch fa-spin"></i>
                    <div class="mt-2">
                        Bezig met optimaliseren...<br />
                        Klik om te annuleren.
                    </div>
                </template>

                <template v-else>
                    <svg
                        class="w-12 h-12 mx-auto text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                    >
                        <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                    <div class="mt-2 text-gray-400">
                        Sleep hier uw afbeelding<span v-if="multiple">(en)</span> of klik op dit vak
                    </div>
                </template>
            </div>

            <input
                class="absolute inset-0 opacity-0 zIndexBackground"
                type="file"
                dusk="image-upload"
                @change="onChange"
                accept="image/*"
                :required="checkRequired()"
            />
        </label>

        <button
            v-if="!uploadCancellationToken && canUseMediaLibrary"
            type="button"
            class="mt-2 mr-2 inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            @click.stop.prevent="openMediaPicker"
        >
            <i class="far fa-photo-video mr-1.5 text-gray-400"></i> Kies uit mediabibliotheek
        </button>

        <button
            v-if="!uploadCancellationToken && imageUrl"
            class="mt-1 mb-2 btn btn-red btn-sm"
            type="button"
            dusk="delete"
            @click="removeImage"
        >
            <i class="mr-1 fa fa-trash"></i> Verwijder
        </button>

        <div
            class="explanation fixed bottom-0 left-5 top-1/2 z-[9999] h-[calc(70vh_+_51px)] w-[25vw] -translate-y-1/2 transform overflow-y-auto rounded bg-white p-4 px-7 py-6"
            style="z-index: 999999; width: calc(25vw - 15px)"
            v-if="openExplanation && this.outputInstruction"
        >
            <div v-html="this.outputInstruction"></div>
        </div>

        <avatar-cropper
            v-if="dropzoneRefPresent && hasCrop"
            ref="avatarCropper"
            :class="openExplanation && this.outputInstruction ? 'explanation-open' : ''"
            :uploadHandler="cropUploadHandler"
            :labels="{
                submit: 'Gereed',
                cancel: 'Annuleer',
            }"
            :trigger="$refs['dropzone']"
            :outputOptions="outputOptionsObject"
            :cropperOptions="cropperOptions"
            :mimes="mimeTypes"
            @changed="checkFileForCropping"
            @cancel="openExplanation = false"
            :style="
                '--cropper-box-outline-margin: ' +
                (this.outputOptionsObject.cropperBoxOutline || '') +
                ';' +
                '--cropper-box-outline-margin-show: ' +
                (this.outputOptionsObject.cropperBoxOutline ? 'block' : 'none') +
                ';'
            "
        />

        <input ref="input" type="hidden" :name="inputName" />
    </div>
</template>

<script>
import axios from '@galaxy/modules/Core/resources/js/axiosBuilder';
import AvatarCropper from 'vue-avatar-cropper';
import freeCropMixin from '@galaxy/modules/Crud/resources/js/form/inputs/freeCropMixin';

export default {
    mixins: [freeCropMixin],

    components: {
        AvatarCropper,
    },

    props: {
        inputName: String,
        multiple: Boolean | String,
        existingMediaId: String,
        existingMediaUrl: String,
        initialValue: String | Number,
        initialAlt: String,
        outputOptions: Object | String,
        required: Boolean | String,
        imgClass: String,
        outputInstruction: String,
    },

    data() {
        return {
            imageUrl: null,
            alt: this.initialAlt || '',
            name: String,
            mounted: false,
            dropzoneRefPresent: false,
            uploadCancellationToken: false,
            uploadPercentage: null,
            dragHover: false,
            mimeTypes:
                'image/png, image/gif, image/jpeg, image/bmp, image/x-icon, image/svg, image/svg+xml, image/webp',
            isRequired: this.required,

            openExplanation: false,

            // Gate the "Kies uit mediabibliotheek" button on the server-injected
            // permission flag (window.galaxy, set in the admin layout / editor).
            canUseMediaLibrary: !!(
                ((window.galaxy || (window.parent && window.parent.galaxy) || {}).permissions || {}).mediaLibrary
            ),
        };
    },

    mounted() {
        this.initInitialValue();
        this.initImageUrl();
        this.initRequestedMimeTypes();

        // @ugly Otherwise avatar croppen element wont find dropzone ref.
        this.dropzoneRefPresent = true;
    },

    methods: {
        initInitialValue() {
            // Set input value.
            this.$refs['input'].value = this.initialValue;
        },

        initImageUrl() {
            // Abort due to no initial value?
            if (!this.initialValue) {
                return;
            }

            // Check if media exists, or if it's a temp file
            if (this.existingMediaId && !isNaN(this.initialValue)) {
                this.imageUrl = this.existingMediaUrl;
            } else {
                this.imageUrl = '/galaxy/crud/admin/form/media/temp?filename=' + encodeURIComponent(this.initialValue);
            }
        },

        onDragOver(event) {
            event.preventDefault();

            this.dragHover = true;
        },

        onDragLeave() {
            this.dragHover = false;
        },

        async onDrop(event) {
            // Stop event.
            event.stopPropagation();
            event.preventDefault();

            this.dragHover = false;

            // Get file.
            let file = _.first(event.dataTransfer?.files || []);

            if (!file) return;

            // Delegate
            if (this.hasCrop && this.isCroppableFile(file)) await this.onCropNewFile(file);
            else await this.onFile(file);
        },

        async onCropNewFile(file) {
            let avatarCropper = this.$refs['avatarCropper'];

            // @note Taken from avatar cropper itself.
            let reader = new FileReader();
            reader.onload = (e) => {
                avatarCropper.dataUrl = e.target.result;
            };
            reader.readAsDataURL(file);

            avatarCropper.filename = file.name || 'unknown';
            avatarCropper.mimeType = avatarCropper.mimeType || file.type;
            avatarCropper.$emit('changed', file, reader);
        },

        async onChange(event) {
            await this.upload(event.target.files[0]);
        },

        async onFile(file) {
            await this.upload(file);
        },

        // Open the media-library picker (registered on the admin window) and apply
        // the chosen image through the normal upload/crop flow.
        openMediaPicker() {
            var picker = window.galaxyMediaPicker || (window.parent && window.parent.galaxyMediaPicker);

            if (!picker || typeof picker.open !== 'function') {
                return;
            }

            var self = this;
            picker.open({ type: 'image' }).then(function (item) {
                if (item && item.url) {
                    // A library item's name is its alt text — carry it over when
                    // it's a real description, otherwise derive one from the file.
                    if (item.name && self.looksDescriptive(item.name)) {
                        self.alt = item.name;
                    } else {
                        self.suggestAltFromFilename(item.file_name || item.name || '');
                    }
                    self.uploadImageFromUrl(item.url, item.file_name);
                }
            });
        },

        async uploadImageFromUrl(url, filename) {
            let file;
            try {
                file = await fetch(url).then((r) => r.blob());
            } catch (e) {
                return;
            }

            if (!file) return;

            file.name = filename;

            if (this.hasCrop && this.isCroppableFile(file)) {
                await this.onCropNewFile(file);
            } else {
                await this.upload(file); // validates mime + uploads + sets preview
            }
        },

        async upload(file) {
            // Abort due to no file selected?
            if (!file || !file.type) return;

            // Notify user due to not an image?
            if (!file.type.match('image.*')) {
                // Notify
                await Swal.fire({
                    title: 'Ongeldig bestand',
                    text: 'Dit bestand is geen afbeelding. Enkel afbeeldingen zijn toegestaan!',
                    icon: 'warning',
                });

                // Abort
                return;
            }

            if (this.validateRequestedMimeType(file.type)) {
                return;
            }

            await this.uploadToServer(file, file.name);

            // Prefill a readable alt from the filename (editor can refine).
            this.suggestAltFromFilename(file.name);

            // Show
            this.imageUrl = (window.URL || window.webkitURL).createObjectURL(file);
        },

        async cropUploadHandler(resp) {
            let dataType = _.split(resp.originalUrl, ';')[0];
            let mimeType = _.split(dataType, ':')[1];

            if (this.validateRequestedMimeType(mimeType)) {
                return;
            }

            // Free crop: render the box (may extend beyond the image) with
            // fillColor padding + maxAspect cap, then upload the result as-is.
            if (this.isFreeCrop) {
                const file = await this.renderFreeCropFile(resp, mimeType);
                if (file) {
                    this.imageUrl = (window.URL || window.webkitURL).createObjectURL(file);
                    await this.uploadToServer(file, this.$refs['avatarCropper'].filename);
                }
                this.openExplanation = false;
                return;
            }

            // Fixed crop.
            this.$refs['avatarCropper'].cropper.getCroppedCanvas(this.outputOptionsObject).toBlob(async (blob) => {
                // Set image url.
                this.imageUrl = (window.URL || window.webkitURL).createObjectURL(blob);

                // Refresh due to uploaded successfully?
                await this.uploadToServer(blob, this.$refs['avatarCropper'].filename);

                this.openExplanation = false;
            }, mimeType || 'image/jpeg');
        },

        async uploadToServer(file, filename) {
            if (this.validateRequestedMimeType(file.type)) {
                return;
            }

            // Init form data.
            let formData = new FormData();

            formData.append('file', file);
            formData.append('filename', filename);

            // Init cancellation token.
            this.uploadCancellationToken = axios.CancelToken.source();

            // Reset upload percentage.
            this.uploadPercentage = 0;

            try {
                // Request
                let response = await axios.post(
                    '/galaxy/crud/' +
                        ($('html').data('prefix') !== undefined ? $('html').data('prefix') : 'admin/') +
                        'form/media/upload',
                    formData,
                    {
                        headers: {
                            'content-type': 'multipart/form-data',
                        },
                        cancelToken: this.uploadCancellationToken.token,
                        onUploadProgress: (progressEvent) => {
                            // Calculate and set upload percentage.
                            this.uploadPercentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        },
                    }
                );

                // Set tmp file.
                this.$refs['input'].value = response.data.tmpFile;

                return true;
            } catch (e) {
                // Is cancel?
                if (axios.isCancel(e)) return false;

                // Notify user.
                this.$toasted.show(
                    '<i class="far fa-exclamation-triangle"></i> Fout bij verwerken afbeelding. Probeer het (later) opnieuw.',
                    { type: 'error' }
                );

                throw e;
            } finally {
                // Mark as done.
                this.uploadCancellationToken = null;
                this.uploadPercentage = null;
            }
        },

        cancelUpload(event) {
            this.openExplanation = false;

            // Stop event to prevent opening file chooser.
            event.stopPropagation();
            event.preventDefault();

            // Cancel
            this.uploadCancellationToken.cancel();
            this.uploadCancellationToken = null;
        },

        async removeImage() {
            // Remove media.
            this.imageUrl = null;
            this.alt = '';

            this.$refs['input'].value = null;
        },

        // Does the alt read like a real description vs a filename/slug/code?
        // Mirrors Galaxy\Core\Services\MediaAltText::looksDescriptive() — keep
        // the two in sync. Core signal: a real space-separated phrase. Letter
        // class covers Latin + common Dutch diacritics.
        looksDescriptive(name) {
            var s = (name || '').trim();
            var L = 'a-zA-ZÀ-ɏ';

            if ((s.match(new RegExp('[' + L + ']', 'g')) || []).length < 3) return false;
            if (/^(screenshot|schermafbeelding|whatsapp)\b/i.test(s)) return false;

            return new RegExp('[' + L + '][' + L + ".'\\-]*\\s+[" + L + ']').test(s);
        },

        // Best-effort human phrase from a filename — mirrors
        // Galaxy\Core\Services\MediaAltText::humanizeFilename(). Used to prefill
        // the alt field on upload so the editor starts from something readable.
        humanizeFilename(filename) {
            var stem = String(filename || '').replace(/\.[^/.]+$/, '');
            stem = stem.replace(/[._\-]+/g, ' ').replace(/\s+/g, ' ').trim();
            if (stem === '') return '';

            var noise = ['kopie','kopieen','copy','def','definitief','final','finaal','klein','groot','web','website','hires','lowres','lores','scan','scanned','edit','edited','bewerkt','naamloos','untitled','image','img','foto','photo','dsc','dscf','dcim','pxl','mvimg','dji','gopr','png','jpg','jpeg','webp'];
            var tokens = stem.split(' ').filter(function (t) {
                if (t.length < 2) return false;
                if (/\d/.test(t)) return false;
                return noise.indexOf(t.toLowerCase()) === -1;
            });

            var out = tokens.join(' ').trim();
            return out === '' ? '' : out.charAt(0).toUpperCase() + out.slice(1);
        },

        // Suggest an alt from a filename, but only when the current alt isn't
        // already a real description (never clobber an editor's text).
        suggestAltFromFilename(filename) {
            if (this.looksDescriptive(this.alt)) return;
            var s = this.humanizeFilename(filename);
            if (s) this.alt = s;
        },

        checkFileForCropping(file) {
            // Check if file is croppable
            if (!this.isCroppableFile(file)) {
                // Stop avatarCropper from opening
                this.$refs['avatarCropper'].$destroy();

                // Continue handling file
                this.onFile(file);
            } else {
                this.openExplanation = true;

                let interval = setInterval(() => {
                    let button = $(this.$refs['avatarCropper'].$el).find('.avatar-cropper-footer > :last-child');
                    if (button.length) {
                        button.focus();
                        clearInterval(interval);
                    }
                }, 100);
            }
        },

        isCroppableFile(file) {
            return _.includes(['image/png', 'image/jpg', 'image/jpeg', 'image/webp'], file.type);
        },

        initRequestedMimeTypes() {
            if (this.outputOptionsObject?.mime) {
                this.mimeTypes = _.isString(this.outputOptionsObject.mime)
                    ? this.outputOptionsObject.mime
                    : this.outputOptionsObject.mime.join(', ');
            }
        },

        validateRequestedMimeType(givenType) {
            let mimeTypes = null;

            _.isString(this.outputOptionsObject?.mime)
                ? (mimeTypes = [this.outputOptionsObject?.mime])
                : (mimeTypes = this.outputOptionsObject?.mime);

            if (mimeTypes && !mimeTypes.includes(givenType)) {
                // Notify
                Swal.fire({
                    title: 'Ongeldig bestand',
                    text: 'Dit bestand heeft niet het gevraagde bestandstype. Enkel ' + mimeTypes.join(', ') + ' zijn toegestaan!',
                    icon: 'warning',
                });

                // Abort
                return true;
            }

            return false;
        },

        checkRequired() {
            if (this.imageUrl) {
                return false;
            }

            return !!this.isRequired;
        },
    },

    computed: {
        // Post the alt next to the image value — keep bracket syntax intact
        // for nested names (`data[image]` → `data[image_alt]`).
        altInputName() {
            return this.inputName.endsWith(']')
                ? this.inputName.slice(0, -1) + '_alt]'
                : this.inputName + '_alt';
        },

        // WCAG hint state — an image is present but its alt doesn't read like a
        // real description (empty, a filename, or a code). Mirrors
        // Galaxy\Core\Services\MediaAltText::looksDescriptive() in PHP.
        altWarning() {
            if (!window.galaxyWcag || !this.imageUrl) {
                return false;
            }
            return !this.looksDescriptive(this.alt);
        },

        // Not reactive.
        hasCrop() {
            return Object.keys(this.outputOptionsObject).length ? true : false;
        },

        cropperOptions() {
            const o = this.outputOptionsObject;
            if (o.cropMode === 'free') {
                return this.freeCropperOptions();   // shared by freeCropMixin
            }
            return {
                aspectRatio: o.aspectRatio || o.width / o.height,
                autoCropArea: 1,
                viewMode: o.viewMode || 1,
                movable: o.moveable || false,
                zoomable: true,
                ...o,
            };
        },

        outputOptionsObject() {
            if (typeof this.outputOptions === 'object') return this.outputOptions;
            else return JSON.parse(this.outputOptions ? this.outputOptions : '{}');
        },
    },
};
</script>

<style lang="scss" scoped>
@use "sass:meta";
// TODO Remove the use of Tailwind in the template, since it is not loaded.

.image {
    font-size: 0;
    transition: opacity 0.2s;

    &.dragHover {
        opacity: 0.5;
    }

    img {
        max-height: 300px;
        max-width: 500px;
    }

    .dropzone {
        height: 150px;
    }

    .zIndexBackground {
        z-index: -1;
    }

    &::v-deep {
        .avatar-cropper {
            .avatar-cropper-mark {
                background: rgba(0, 0, 0, 0.3);
                backdrop-filter: blur(3px);
            }

            .avatar-cropper-container {
                @apply transform transition;
            }

            &.explanation-open .avatar-cropper-container {
                @apply translate-x-[calc(15vw_-_30px)];
            }

            // Free-crop controls bar (presets + reset/lock) — shared partial.
            // meta.load-css = the @use-era replacement for a nested @import
            // (sibling file; resolved relative to this component).
            @include meta.load-css("free-crop-controls");
        }

        .cropper-center {
            &:before {
                width: 14px;
                left: -6px;
            }

            &:after {
                height: 14px;
                top: -6px;
            }
        }

        .avatar-cropper-image-container {
            height: 100%;
            max-height: 70vh !important;
            max-width: 70vw !important;
            width: 100%;

            .cropper-crop-box:before {
                content: '';
                position: absolute;
                inset: 0;
                margin: var(--cropper-box-outline-margin);
                border: 1px solid cyan;
                z-index: 1;
                display: var(--cropper-box-outline-margin-show);
                pointer-events: none;
            }
        }

        .avatar-cropper-btn {
            font-size: 14px;

            &:focus {
                &:last-child {
                    background: #55ce63 !important;
                    color: white;
                }
            }

            &:hover {
                &:first-child {
                    background: #009efb !important;
                }

                &:last-child {
                    background: #55ce63 !important;
                }
            }
        }
    }
}
</style>
